import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import CountUp from "react-countup";
import { Quote, Star, ArrowUpRight, BadgeCheck } from "lucide-react";
import { googleReviews, testimonials } from "../data/mockData";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const initials = (name) =>
  name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

const StarRating = ({ rating }) => (
  <div className="tz-stars" aria-label={`${rating} out of 5 stars`}>
    {[...Array(5)].map((_, i) => (
      <Star key={i} size={16} fill={i < rating ? "#c54b2a" : "#d8d8d8"} stroke={i < rating ? "#c54b2a" : "#d8d8d8"} />
    ))}
  </div>
);

const TestimonialsSection = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <section className="inner-section tz-section" ref={ref}>
      <div className="inner-container">
        <motion.div
          className="tz-header"
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={fadeUp}
        >
          <span className="section-subtitle">Client Testimonials</span>
          <h2 className="section-title">What Our Clients Say</h2>
          <div className="section-line" />
          <p className="tz-header-desc">
            Trusted by engineers, founders, and research teams across Bangalore and 20+ countries.
            Every review below reflects a real project delivered from our workshop.
          </p>
        </motion.div>

        <div className="tz-summary-grid">
          <motion.div
            className="tz-summary-card"
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.15 }}
          >
            <div className="tz-score-row">
              <span className="tz-score-number">
                {inView && <CountUp end={googleReviews.score} decimals={1} duration={2} />}
              </span>
              <span className="tz-score-outof">/ 5</span>
            </div>
            <StarRating rating={5} />
            <p className="tz-count">
              <BadgeCheck size={18} />
              {googleReviews.count}+ verified reviews on Google
            </p>
            <a
              href={googleReviews.url}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="tz-google-btn"
            >
              View all reviews on Google
              <ArrowUpRight size={16} />
            </a>
          </motion.div>

          <motion.div
            className="tz-embed"
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.25 }}
          >
            <div className="tz-embed-head">
              <span className="tz-live-dot" />
              Live · synced with our Google Business profile
            </div>
            <iframe
              className="tz-embed-frame"
              src={googleReviews.embedUrl}
              title="3D Printzkart - Live Reviews on Google Maps"
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
            />
          </motion.div>
        </div>

        <div className="tz-grid">
          {testimonials.map((t, i) => (
            <motion.article
              key={i}
              className="tz-card"
              initial={{ opacity: 0, y: 24 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.45, delay: i * 0.08 }}
            >
              <Quote size={26} className="tz-quote-icon" />
              <StarRating rating={t.rating} />
              <p className="tz-text">“{t.text}”</p>
              <div className="tz-author">
                <span className="tz-avatar">{initials(t.name)}</span>
                <div className="tz-author-meta">
                  <h4>{t.name}</h4>
                  <span className="tz-role">{t.role}</span>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;